import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Header,
  SpaceBetween,
  Table,
} from "@amzn/awsui-components-react";
import {
  InvestigationAttachment,
  InvestigationEvent,
  Stage,
} from "../../lusca-search-api/generated-src";
import { InvestigationsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import { formatTimestamp, submitOnModEnter } from "./investigationUtils";
import { ClampedCommentMarkdown } from "./CommentMarkdown";
import { CommentInput } from "./CommentInput";
import { useAttachmentUploads } from "../../hooks/useAttachmentUploads";
import useFittedColumns from "../../hooks/useFittedColumns";
import { AttachmentDropzone } from "./attachments/AttachmentDropzone";
import { AttachmentList } from "./attachments/AttachmentList";
import { sameKeys } from "./attachments/attachmentUtils";
import {
  InlineAttachmentProvider,
  useInlineAttachments,
  withoutInline,
} from "./attachments/inlineAttachments";
import { OneLine } from "../common/OneLine";

interface InvestigationTimelineProps {
  stage: Stage;
  investigationId: string;
  initialEvents: InvestigationEvent[];
  initialNextToken?: string;
}

const EVENT_LABELS: Record<string, string> = {
  CREATED: "Investigation created",
  CLAIMED: "Ownership claimed",
  OWNERSHIP_TAKEN_OVER: "Ownership taken over",
  OWNERSHIP_TRANSFERRED: "Ownership transferred",
  OWNERSHIP_RELEASED: "Ownership released",
  CONTRIBUTOR_ADDED: "Contributor added",
  CONTRIBUTOR_REMOVED: "Contributor removed",
  FOLLOWED: "Followed",
  UNFOLLOWED: "Stopped following",
  POPULATION_FROZEN: "Population snapshot frozen",
  POPULATION_REFRESHED: "Population refreshed",
  POPULATION_REFRESH_STARTED: "Population refresh started",
  POPULATION_RECONCILED: "Population reconciled",
  ITEMS_ASSIGNED: "Items assigned",
  ITEMS_RELEASED: "Items released",
  STATUS_CHANGED: "Status changed",
  TITLE_CHANGED: "Renamed",
  FIELDS_UPDATED: "Details updated",
  // Findings/hypotheses were retired in favour of comments; labels stay so older audit
  // logs still read correctly.
  FINDING_ADDED: "Finding added",
  FINDING_EDITED: "Finding edited",
  FINDING_REMOVED: "Finding removed",
  HYPOTHESIS_ADDED: "Hypothesis added",
  HYPOTHESIS_EDITED: "Hypothesis edited",
  HYPOTHESIS_REMOVED: "Hypothesis removed",
  LINK_ADDED: "Link added",
  LINK_EDITED: "Link edited",
  LINK_REMOVED: "Link removed",
  TASK_LINKED: "Task linked",
  TASK_UNLINKED: "Task unlinked",
  TASK_STATE_PUSHED: "Task updated to match investigation",
  COMMENT: "Comment",
  COMMENT_EDITED: "Comment edited",
  COMMENT_DELETED: "Comment deleted",
  REDRIVE_LAUNCHED: "Redrive launched",
  REDRIVE_COMPLETED: "Redrive completed",
  REDRIVE_FAILED: "Redrive failed",
  REDRIVE_RETURN_DETECTED: "Returned messages detected",
  REDRIVE_INCOMPLETE: "Items not redriven (still in DLQ)",
  MONITORING_CLEAR: "Monitoring passed",
  RULE_WIDENED: "Rule widened",
  RETURNED_ITEMS_RELEASED: "Returned items released",
  TRACKSTARS_WIDEN_SUGGESTED: "TrackStars found more",
  RECOUNTED: "Items recounted",
  ITEMS_TAKEN: "Items taken by a narrower case",
  RULE_OVERLAP_DETECTED: "Rule overlap",
  STALE_REMINDER_SENT: "Stale reminder sent",
  DOCUMENT_REDRIVE_LAUNCHED: "Order redrive launched",
  DOCUMENT_REDRIVE_COMPLETED: "Order redrive completed",
  DOCUMENT_REDRIVE_FAILED: "Order redrive failed",
  DOCUMENT_REDRIVE_REMINDER_SET: "Reminder set for the CBE check",
  DOCUMENT_REDRIVE_UNCONFIRMED: "CBE confirmation taken back",
  DELETION_REQUESTED: "Deletion requested (Draft MCM)",
  DELETION_STARTED: "Deletion started",
  DELETION_EXECUTED: "Deletion executed",
  DELETION_FAILED: "Deletion failed",
  DELETION_VERIFIED: "Deletion verified in snapshot",
  DELETION_INCOMPLETE: "Deleted items still in DLQ",
  DELETION_CANCELLED: "Deletion request cancelled",
  CHILD_CREATED: "Narrower case split out",
  SPLIT_BY_LIST: "Split by list",
  CREATED_FROM_LIST: "Created from a list",
  RECURRENCE_LINKED: "Recurrence linked",
  DUPLICATE_CLOSED: "Closed as a duplicate",
  DUPLICATE_LINKED: "Duplicate attempt recorded",
  REOPENED: "Reopened",
  ARTIFACT_UPLOAD_REQUESTED: "Artifact uploaded",
  TRACKSTARS_RUN_REQUESTED: "TrackStars run started",
  TRACKSTARS_RUN_COMPLETED: "TrackStars run finished",
  // Blocking on a CR (specs/dlq-investigations/cr-blocker).
  CODE_REVIEW_MERGED: "Blocking CR merged",
  CODE_REVIEW_DISCARDED: "Blocking CR discarded",
  CODE_REVIEW_INVALID: "Blocking CR can't be followed",
  QUEUED_REDRIVE_NOT_RUN: "Queued redrive not run",
};

export function eventLabel(event: InvestigationEvent): string {
  return (
    EVENT_LABELS[event.type || ""] ||
    (event.type || "").replace(/_/g, " ").toLowerCase()
  );
}

type ChangeRecord = Record<string, unknown> | undefined;

const changeText = (change: ChangeRecord): string | undefined =>
  typeof change?.text === "string" ? change.text : undefined;

const changeAttachments = (
  change: ChangeRecord
): InvestigationAttachment[] | undefined =>
  change && Array.isArray(change.attachments)
    ? (change.attachments as InvestigationAttachment[])
    : undefined;

/**
 * What a comment says now. The audit log is append-only: `text` is what was posted and never
 * changes; an edit stamps the latest wording in `currentText` and records itself as a
 * COMMENT_EDITED event. A deleted comment says nothing.
 */
export function commentText(event: InvestigationEvent): string {
  if (event.deletedAt) return "";
  return event.currentText ?? event.text ?? "";
}

/** The files a comment shows now (see commentText). */
export function commentAttachments(
  event: InvestigationEvent
): InvestigationAttachment[] {
  if (event.deletedAt) return [];
  return event.currentAttachments ?? event.attachments ?? [];
}

const FIELD_LABELS: Record<string, string> = {
  description: "description",
  blocker: "blocker",
  conclusion: "conclusion",
  title: "title",
};

export function eventDetail(event: InvestigationEvent): string {
  if (event.type === "STATUS_CHANGED") {
    const previous = (event.previous as { status?: string } | undefined)
      ?.status;
    const next = (event.new as { status?: string } | undefined)?.status;
    const transition = `${previous ?? "?"} → ${next ?? "?"}`;
    const data = event.data as
      | { reason?: string; codeReviewId?: string; deployedRevision?: string }
      | undefined;
    if (data?.reason === "CR_DEPLOYED" && data.codeReviewId) {
      // Lusca unblocked the case (or queued its redrive) because the CR was deployed.
      const where = data.deployedRevision ? ` (${data.deployedRevision})` : "";
      return `${
        event.text || `${data.codeReviewId} was deployed`
      }${where}: ${transition}`;
    }
    return transition;
  }
  if (event.type === "FIELDS_UPDATED" && !event.text) {
    const fields = Object.keys(
      (event.new as Record<string, unknown> | undefined) ?? {}
    ).map((key) => FIELD_LABELS[key] ?? key);
    return fields.length ? `Changed ${fields.join(", ")}` : "";
  }
  if (event.type === "COMMENT_EDITED") {
    const previous = changeText(event.previous as ChangeRecord);
    const next = changeText(event.new as ChangeRecord) ?? event.text ?? "";
    return previous === undefined ? next : `${previous} → ${next}`;
  }
  if (event.type === "COMMENT_DELETED") {
    const previous = changeText(event.previous as ChangeRecord);
    return previous ? `Deleted: ${previous}` : "";
  }
  if (event.type === "LINK_EDITED") {
    const previous = event.previous as
      | { label?: string; url?: string }
      | undefined;
    const next = event.new as { label?: string; url?: string } | undefined;
    if (previous && next) {
      const from = previous.label || previous.url || "?";
      const to = next.label || next.url || "?";
      return from === to ? to : `${from} → ${to}`;
    }
  }
  return event.text || "";
}

const LONG_TEXT_CHARS = 200;
const LONG_TEXT_NEWLINES = 3;

/** Detail text that would blow up the row height gets clamped behind "Show more". */
export function isLongDetail(text: string): boolean {
  return (
    text.length > LONG_TEXT_CHARS ||
    (text.match(/\n/g) || []).length > LONG_TEXT_NEWLINES
  );
}

const TEXT_STYLE: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const CLAMP_STYLE: React.CSSProperties = {
  ...TEXT_STYLE,
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const EXPANDED_STYLE: React.CSSProperties = {
  ...TEXT_STYLE,
  maxHeight: 280,
  overflow: "auto",
};

export const DetailText: React.FC<{
  text: string;
  markdown?: boolean;
  /** @mentions to highlight (markdown only). */
  mentions?: readonly string[];
}> = ({ text, markdown = false, mentions }) => {
  const [expanded, setExpanded] = useState(false);
  if (markdown)
    return <ClampedCommentMarkdown text={text} mentions={mentions} />;
  if (!isLongDetail(text)) {
    return <span style={TEXT_STYLE}>{text}</span>;
  }
  return (
    <SpaceBetween size="xxs">
      <div
        data-testid="detail-text"
        data-expanded={expanded ? "true" : "false"}
        style={expanded ? EXPANDED_STYLE : CLAMP_STYLE}
      >
        {text}
      </div>
      <Button
        variant="inline-link"
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? "Show less" : "Show more"}
      </Button>
    </SpaceBetween>
  );
};

const renderTime = (event: InvestigationEvent) => (
  <Box color="text-body-secondary" fontSize="body-s">
    <OneLine>{formatTimestamp(event.timestamp)}</OneLine>
  </Box>
);

const renderEvent = (event: InvestigationEvent) => (
  <OneLine title={eventLabel(event)}>
    <strong>{eventLabel(event)}</strong>
  </OneLine>
);

const renderActor = (event: InvestigationEvent) => (
  <OneLine>{event.actor}</OneLine>
);

/** ui / mcp / system origin of an event, colour-coded. */
export const SourceBadge: React.FC<{ source?: string }> = ({ source }) => (
  <Badge
    color={source === "mcp" ? "blue" : source === "system" ? "grey" : "green"}
  >
    {source}
  </Badge>
);

const renderSource = (event: InvestigationEvent) => (
  <SourceBadge source={event.source} />
);

interface CommentEditorProps {
  stage: Stage;
  investigationId: string;
  initialText: string;
  initialAttachments?: InvestigationAttachment[];
  saving: boolean;
  onCancel: () => void;
  /**
   * `attachments` is the full new list of `{key}`s (kept, added or removed), or undefined when
   * they did not change (the server then keeps the current ones).
   */
  onSave: (text: string, attachments?: { key: string }[]) => void;
}

export const CommentEditor: React.FC<CommentEditorProps> = ({
  stage,
  investigationId,
  initialText,
  initialAttachments = [],
  saving,
  onCancel,
  onSave,
}) => {
  const [text, setText] = useState(initialText);
  const uploads = useAttachmentUploads(
    stage,
    investigationId,
    initialAttachments
  );
  const insertInline = useInlineAttachments(uploads, text, setText);
  const trimmed = text.trim();
  const attachmentsChanged = !sameKeys(uploads.refs, initialAttachments);
  const unchanged = trimmed === initialText.trim() && !attachmentsChanged;
  const disabled =
    (!trimmed && uploads.refs.length === 0) || unchanged || uploads.uploading;
  const save = () =>
    onSave(trimmed, attachmentsChanged ? uploads.refs : undefined);
  return (
    <SpaceBetween size="xs">
      <AttachmentDropzone
        uploads={uploads}
        disabled={saving}
        onFilesAdded={insertInline}
      >
        <InlineAttachmentProvider pending={uploads.items}>
          <CommentInput
            ariaLabel="Edit comment"
            stage={stage}
            value={text}
            onChange={setText}
            onKeyDown={submitOnModEnter(save, disabled || saving)}
            rows={4}
          />
        </InlineAttachmentProvider>
      </AttachmentDropzone>
      {/* Right-aligned, like "Add comment" under the new-comment box. */}
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={disabled}
            onClick={save}
          >
            Save
          </Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
};

const flattenValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "(none)";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length ? value.map(flattenValue).join(", ") : "(none)";
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([key, v]) => key !== "attachments" && v !== undefined && v !== null
    );
    if (!entries.length) return "(none)";
    return entries.map(([key, v]) => `${key}: ${flattenValue(v)}`).join("; ");
  }
  return String(value);
};

const BEFORE_STYLE: React.CSSProperties = {
  ...TEXT_STYLE,
  textDecoration: "line-through",
  opacity: 0.75,
};

/** Old → new for an event that changed something, one row per field. */
const ChangeDetail: React.FC<{
  stage: Stage;
  investigationId: string;
  event: InvestigationEvent;
}> = ({ stage, investigationId, event }) => {
  const previous = (event.previous as Record<string, unknown>) ?? {};
  const next = (event.new as Record<string, unknown>) ?? {};
  const keys = Array.from(
    new Set([...Object.keys(previous), ...Object.keys(next)])
  ).filter((key) => key !== "attachments");
  const previousAttachments = changeAttachments(previous);
  const nextAttachments = changeAttachments(next);
  const markdown =
    event.type === "COMMENT_EDITED" || event.type === "COMMENT_DELETED";
  return (
    <InlineAttachmentProvider
      stage={stage}
      investigationId={investigationId}
      attachments={[...(previousAttachments || []), ...(nextAttachments || [])]}
    >
      <SpaceBetween size="xxs">
        {keys.map((key) => {
          const before = previous[key];
          const after = next[key];
          const showLabel = keys.length > 1 || !["text"].includes(key);
          return (
            <div key={key} data-testid={`change-${key}`}>
              {showLabel && (
                <Box color="text-body-secondary" fontSize="body-s">
                  {FIELD_LABELS[key] ?? key}
                </Box>
              )}
              {key in previous && (
                <Box data-testid="change-before">
                  {markdown && typeof before === "string" ? (
                    <div style={BEFORE_STYLE}>
                      <DetailText text={before} markdown />
                    </div>
                  ) : (
                    <span style={BEFORE_STYLE}>{flattenValue(before)}</span>
                  )}
                </Box>
              )}
              {key in next && (
                <Box data-testid="change-after">
                  {key in previous && (
                    <Box
                      color="text-body-secondary"
                      fontSize="body-s"
                      display="inline"
                      margin={{ right: "xxs" }}
                    >
                      →
                    </Box>
                  )}
                  {markdown && typeof after === "string" ? (
                    <DetailText text={after} markdown />
                  ) : (
                    <span style={TEXT_STYLE}>{flattenValue(after)}</span>
                  )}
                </Box>
              )}
            </div>
          );
        })}
        {(previousAttachments || nextAttachments) && (
          <SpaceBetween size="xxs">
            {previousAttachments && (
              <Box>
                <Box color="text-body-secondary" fontSize="body-s">
                  attachments before
                </Box>
                <AttachmentList
                  stage={stage}
                  investigationId={investigationId}
                  attachments={previousAttachments}
                  compact
                />
              </Box>
            )}
            {nextAttachments && (
              <Box>
                <Box color="text-body-secondary" fontSize="body-s">
                  attachments after
                </Box>
                <AttachmentList
                  stage={stage}
                  investigationId={investigationId}
                  attachments={nextAttachments}
                  compact
                />
              </Box>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </InlineAttachmentProvider>
  );
};

const hasChange = (event: InvestigationEvent) =>
  (event.previous && Object.keys(event.previous).length > 0) ||
  (event.new && Object.keys(event.new).length > 0);

/**
 * Immutable audit trail; every entry carries actor, source (ui/mcp/system) and timestamp.
 * Read-only by design: a COMMENT row always shows what was posted, an edit or deletion is its
 * own row showing old → new, and comments are edited from the Comments tab, never from here.
 */
export const InvestigationTimeline: React.FC<InvestigationTimelineProps> = ({
  stage,
  investigationId,
  initialEvents,
  initialNextToken,
}) => {
  const [extra, setExtra] = useState<InvestigationEvent[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(
    initialNextToken
  );
  const [loading, setLoading] = useState(false);

  const events = [...initialEvents, ...extra];

  const loadMore = async () => {
    if (!nextToken) return;
    setLoading(true);
    try {
      const { data } =
        await InvestigationsApiFactory().getInvestigationTimeline(
          stage,
          investigationId,
          50,
          nextToken
        );
      setExtra((prev) => [...prev, ...(data.events || [])]);
      setNextToken(data.nextToken);
    } finally {
      setLoading(false);
    }
  };

  const renderDetail = (event: InvestigationEvent) => {
    if (event.type === "COMMENT") {
      // The original post, as written. Later wording lives in the COMMENT_EDITED rows above it.
      return (
        <SpaceBetween size="xxs">
          {event.deletedAt && (
            <Box color="text-body-secondary" fontSize="body-s">
              <i>
                Deleted by {event.deletedBy || "unknown"} ·{" "}
                {formatTimestamp(event.deletedAt)}
              </i>
            </Box>
          )}
          <div style={event.deletedAt ? { opacity: 0.6 } : undefined}>
            <InlineAttachmentProvider
              stage={stage}
              investigationId={investigationId}
              attachments={event.attachments}
            >
              <DetailText
                text={event.text || ""}
                markdown
                mentions={event.mentions}
              />
            </InlineAttachmentProvider>
          </div>
          <AttachmentList
            stage={stage}
            investigationId={investigationId}
            attachments={withoutInline(event.attachments, event.text || "")}
            compact
          />
          {event.editedAt && (
            <Box color="text-body-secondary" fontSize="body-s">
              <i>
                Original wording; edited by {event.editedBy || "unknown"} ·{" "}
                {formatTimestamp(event.editedAt)}
              </i>
            </Box>
          )}
        </SpaceBetween>
      );
    }
    if (
      (event.type === "COMMENT_EDITED" ||
        event.type === "COMMENT_DELETED" ||
        event.type === "FIELDS_UPDATED" ||
        event.type === "LINK_EDITED" ||
        event.type === "TITLE_CHANGED") &&
      hasChange(event)
    ) {
      return (
        <ChangeDetail
          stage={stage}
          investigationId={investigationId}
          event={event}
        />
      );
    }
    return (
      <SpaceBetween size="xxs">
        <DetailText text={eventDetail(event)} />
        <AttachmentList
          stage={stage}
          investigationId={investigationId}
          attachments={event.attachments}
          compact
        />
      </SpaceBetween>
    );
  };

  // Detail takes whatever the fixed columns leave; drag a header edge to resize (kept per browser).
  const fittedTable = useFittedColumns<InvestigationEvent>(
    "lusca.investigations.timeline.columnWidths.v1",
    [
      { id: "time", header: "Time", cell: renderTime, width: 180 },
      { id: "event", header: "Event", cell: renderEvent, width: 200 },
      {
        id: "detail",
        header: "Detail",
        cell: renderDetail,
        fill: true,
        minWidth: 260,
      },
      { id: "actor", header: "Actor", cell: renderActor, width: 140 },
      { id: "source", header: "Source", cell: renderSource, width: 100 },
    ]
  );

  return (
    <div ref={fittedTable.containerRef}>
      <Table
        key={fittedTable.tableKey}
        header={
          <Header
            variant="h3"
            counter={`(${events.length}${nextToken ? "+" : ""})`}
            description="Every change to this investigation, as it happened. Entries are never rewritten: editing or deleting a comment adds a new entry showing what changed."
          >
            Activity
          </Header>
        }
        items={events}
        trackBy="eventId"
        wrapLines
        {...fittedTable.tableProps}
        empty={<Box textAlign="center">No activity yet</Box>}
        footer={
          nextToken ? (
            <Box textAlign="center">
              <Button
                onClick={() => {
                  loadMore().catch(console.error);
                }}
                loading={loading}
              >
                Load older activity
              </Button>
            </Box>
          ) : undefined
        }
      />
    </div>
  );
};

export default InvestigationTimeline;
